import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  ApiError,
  decideAnalysis,
  decisionAudioUrl,
  getReassessItemStatus,
  getReassessStatus,
  listMboAnalyses,
  triggerReassessItem,
  triggerReassessRun,
} from '../api/orchestratorClient'

// essms-mbo Analysis Stage enum -> chip config
const STAGE_CONFIG = {
  CREATED: { label: 'Created', color: 'var(--text-muted)', bg: 'var(--border)' },
  REVIEWED: { label: 'Reviewed', color: 'var(--series-1)', bg: 'var(--sequential-100)' },
  SUBMITTED: { label: 'Awaiting Review', color: 'var(--status-warning)', bg: '#fef3c7' },
  APPROVED: { label: 'Approved', color: 'var(--status-good)', bg: '#dcfce7' },
  REJECTED: { label: 'Rejected', color: 'var(--status-critical)', bg: '#fee2e2' },
}

// essms-mbo Analysis.aiFixStatus -> chip config (FIXABLE/UNFIXABLE/FAILED/null,
// see Analysis.java's ai_fix_status comment). 'all'/'UNSET' are UI-only buckets,
// not real backend values.
const FIX_STATUS_CONFIG = {
  all: { label: 'All', color: 'var(--text-primary)' },
  FIXABLE: { label: 'AI can fix', color: 'var(--status-good)' },
  UNFIXABLE: { label: 'AI cannot fix', color: 'var(--status-critical)' },
  FAILED: { label: 'Autofix failed', color: 'var(--status-warning)' },
  UNSET: { label: 'Unclassified', color: 'var(--text-muted)' },
}

function fixStatusOf(a) {
  return a.aiFixStatus || 'UNSET'
}

function scopeOf(a) {
  if (a.issueId) return { type: 'Issue', id: a.issueId, icon: '🐛' }
  if (a.requirementId) return { type: 'Requirement', id: a.requirementId, icon: '📝' }
  if (a.sopId) return { type: 'SOP', id: a.sopId, icon: '📋' }
  if (a.milestoneId) return { type: 'Milestone', id: a.milestoneId, icon: '🚩' }
  return { type: 'Unscoped', id: null, icon: '📄' }
}

// Challenge (item 2) only works for Issue/Requirement/SOP-scoped analyses --
// reassess_single_item looks the scope id up via the MBO tree walk, which only
// enumerates those three ticket kinds, not Milestones.
function canChallenge(scope) {
  return !!scope.id && scope.type !== 'Milestone' && scope.type !== 'Unscoped'
}

function confidenceColor(pct) {
  if (pct == null) return 'var(--text-muted)'
  if (pct >= 75) return 'var(--status-good)'
  if (pct >= 50) return 'var(--status-warning)'
  return 'var(--status-critical)'
}

function ConfidenceBar({ value }) {
  const pct = value == null ? null : Math.max(0, Math.min(100, Number(value)))
  return (
    <div className="conf-wrap" title={pct == null ? 'No confidence score recorded' : `AI confidence: ${pct}%`}>
      <div className="conf-track">
        <div
          className="conf-fill"
          style={{ width: `${pct ?? 0}%`, background: confidenceColor(pct) }}
        />
      </div>
      <span className="conf-label" style={{ color: confidenceColor(pct) }}>
        {pct == null ? '—' : `${Math.round(pct)}%`}
      </span>
      <style jsx>{`
        .conf-wrap {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
        }
        .conf-track {
          flex: 1;
          height: 6px;
          min-width: 60px;
          border-radius: 999px;
          background: var(--border);
          overflow: hidden;
        }
        .conf-fill {
          height: 100%;
          border-radius: 999px;
          transition: width 0.3s ease;
        }
        .conf-label {
          font-size: 12px;
          font-weight: 700;
          font-variant-numeric: tabular-nums;
          white-space: nowrap;
        }
      `}</style>
    </div>
  )
}

/** Renders the "Key: value" line-oriented analysis description as labeled
 *  sections instead of one text blob. Unrecognized lines flow into the
 *  preceding section. */
function AnalysisDescription({ text }) {
  if (!text) return <p className="no-desc">No description.</p>
  const KNOWN = [
    'Root cause / need', 'Approach', 'Why', 'Risks', 'Test strategy',
    'Estimated effort', 'Estimated cost', 'AI confidence', 'Change type',
    'Classes/components touched', 'Services touched', 'Files likely touched',
    'Downstream services impacted', 'Contract compatibility',
    'Required contract tests', 'Timeline', 'Note', 'Code-verified',
  ]
  const sections = []
  let current = { label: null, lines: [] }
  for (const line of text.split('\n')) {
    const match = KNOWN.map(k => (line.startsWith(k + ':') ? k : null)).find(Boolean)
    if (match) {
      if (current.lines.length || current.label) sections.push(current)
      current = { label: match, lines: [line.slice(match.length + 1).trim()] }
    } else {
      current.lines.push(line)
    }
  }
  if (current.lines.length || current.label) sections.push(current)

  return (
    <div className="desc-sections">
      {sections.map((s, i) => (
        <div key={i} className="desc-section">
          {s.label && <div className="desc-label">{s.label}</div>}
          <p className="desc-text">{s.lines.join('\n').trim()}</p>
        </div>
      ))}
      <style jsx>{`
        .desc-sections {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .desc-label {
          font-size: 11px;
          font-weight: 700;
          color: var(--text-muted);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 2px;
        }
        .desc-text {
          font-size: 13px;
          color: var(--text-primary);
          line-height: 1.55;
          white-space: pre-wrap;
        }
      `}</style>
    </div>
  )
}

/** Microphone voice-note recorder used inside the Accept/Reject flow.
 *  Records via MediaRecorder; hands the finished clip up as a base64 string. */
function VoiceRecorder({ onRecorded, disabled }) {
  const [recording, setRecording] = useState(false)
  const [clipUrl, setClipUrl] = useState(null)
  const [seconds, setSeconds] = useState(0)
  const [error, setError] = useState(null)
  const recorderRef = useRef(null)
  const chunksRef = useRef([])
  const timerRef = useRef(null)

  const stopTimer = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
  }

  const start = async () => {
    setError(null)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : ''
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined)
      chunksRef.current = []
      rec.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data) }
      rec.onstop = () => {
        stream.getTracks().forEach(t => t.stop())
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' })
        setClipUrl(prev => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(blob) })
        const reader = new FileReader()
        reader.onloadend = () => {
          const base64 = String(reader.result).split(',')[1] || null
          onRecorded(base64 ? { base64, mime: blob.type } : null)
        }
        reader.readAsDataURL(blob)
      }
      recorderRef.current = rec
      rec.start()
      setRecording(true)
      setSeconds(0)
      timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000)
    } catch (e) {
      setError('Microphone unavailable — check browser permission.')
    }
  }

  const stop = () => {
    stopTimer()
    setRecording(false)
    recorderRef.current?.stop()
  }

  const discard = () => {
    if (clipUrl) URL.revokeObjectURL(clipUrl)
    setClipUrl(null)
    setSeconds(0)
    onRecorded(null)
  }

  useEffect(() => () => { stopTimer(); if (clipUrl) URL.revokeObjectURL(clipUrl) }, [clipUrl])

  return (
    <div className="voice-recorder">
      {!recording && !clipUrl && (
        <button type="button" className="rec-btn" onClick={start} disabled={disabled}>
          🎙️ Record voice note
        </button>
      )}
      {recording && (
        <button type="button" className="rec-btn recording" onClick={stop}>
          ⏹ Stop ({seconds}s)
        </button>
      )}
      {clipUrl && !recording && (
        <div className="clip-row">
          <audio controls src={clipUrl} className="clip-audio" />
          <button type="button" className="discard-btn" onClick={discard} title="Discard recording">✕</button>
        </div>
      )}
      {error && <span className="rec-error">{error}</span>}
      <style jsx>{`
        .voice-recorder {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        .rec-btn {
          padding: 6px 12px;
          border-radius: 6px;
          border: 1px solid var(--border);
          background: var(--page-plane);
          color: var(--text-primary);
          font-size: 12px;
          font-weight: 500;
        }
        .rec-btn.recording {
          border-color: var(--status-critical);
          color: var(--status-critical);
          animation: recpulse 1.2s infinite;
        }
        @keyframes recpulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.55; }
        }
        .rec-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .clip-row {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .clip-audio {
          height: 32px;
          max-width: 260px;
        }
        .discard-btn {
          width: 26px;
          height: 26px;
          border-radius: 50%;
          border: 1px solid var(--border);
          color: var(--text-muted);
          background: var(--page-plane);
          font-size: 12px;
        }
        .discard-btn:hover {
          color: var(--status-critical);
          border-color: var(--status-critical);
        }
        .rec-error {
          font-size: 12px;
          color: var(--status-critical);
        }
      `}</style>
    </div>
  )
}

function DecisionPanel({ analysis, onDecided }) {
  const [comment, setComment] = useState('')
  const [audio, setAudio] = useState(null)
  const [submitting, setSubmitting] = useState(null) // 'approve' | 'reject' | null
  const [error, setError] = useState(null)

  const submit = async decision => {
    setSubmitting(decision)
    setError(null)
    try {
      await decideAnalysis(analysis.id, {
        decision,
        comment,
        decidedBy: 'audit-dashboard',
        audioBase64: audio?.base64,
        audioMime: audio?.mime,
      })
      onDecided()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to record the decision')
    } finally {
      setSubmitting(null)
    }
  }

  return (
    <div className="decision-panel">
      <h4>Your decision</h4>
      <textarea
        className="decision-comment"
        placeholder="Optional comment on why you accept or reject this analysis…"
        value={comment}
        onChange={e => setComment(e.target.value)}
        rows={2}
      />
      <VoiceRecorder onRecorded={setAudio} disabled={!!submitting} />
      <div className="decision-actions">
        <button
          className="approve-btn"
          disabled={!!submitting}
          onClick={() => submit('approve')}
        >
          {submitting === 'approve' ? 'Saving…' : '✓ Accept analysis'}
        </button>
        <button
          className="reject-btn"
          disabled={!!submitting}
          onClick={() => submit('reject')}
        >
          {submitting === 'reject' ? 'Saving…' : '✕ Reject analysis'}
        </button>
        {audio && <span className="audio-attached">🎙️ voice note attached</span>}
      </div>
      {error && <div className="decision-error">⚠️ {error}</div>}
      <style jsx>{`
        .decision-panel {
          display: flex;
          flex-direction: column;
          gap: 10px;
          padding: 14px;
          border: 1px solid var(--border);
          border-radius: 8px;
          background: var(--page-plane);
        }
        .decision-panel h4 {
          font-size: 13px;
          font-weight: 600;
          color: var(--text-primary);
        }
        .decision-comment {
          width: 100%;
          padding: 8px 10px;
          border: 1px solid var(--border);
          border-radius: 6px;
          background: var(--surface-1);
          color: var(--text-primary);
          font-size: 13px;
          font-family: inherit;
          resize: vertical;
        }
        .decision-actions {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }
        .approve-btn, .reject-btn {
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          color: white;
        }
        .approve-btn { background: var(--status-good); }
        .reject-btn { background: var(--status-critical); }
        .approve-btn:disabled, .reject-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .audio-attached {
          font-size: 12px;
          color: var(--text-secondary);
        }
        .decision-error {
          font-size: 12px;
          color: var(--status-critical);
        }
      `}</style>
    </div>
  )
}

/** Item 2: re-assess ONE analysis with the reviewer's challenge fed into the fresh
 *  pass -- POST /admin/mbo/reassess/item, polled until done. Distinct from
 *  DecisionPanel's Accept/Reject: a challenge asks the AI to re-think the analysis
 *  itself, not record a human verdict on the current one. */
function ChallengePanel({ scopeId, onChallenged }) {
  const [text, setText] = useState('')
  const [state, setState] = useState('idle') // idle | running | done | failed
  const [error, setError] = useState(null)
  const pollRef = useRef(null)

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current) }, [])

  const poll = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current)
    pollRef.current = setInterval(async () => {
      try {
        const s = await getReassessItemStatus()
        if (s.state !== 'running') {
          clearInterval(pollRef.current)
          pollRef.current = null
          setState(s.state === 'failed' ? 'failed' : 'done')
          if (s.state === 'failed') setError(s.error || 'Challenge re-assessment failed')
          else { setText(''); onChallenged() }
        }
      } catch { /* transient poll failure -- keep polling */ }
    }, 4000)
  }, [onChallenged])

  const submit = async () => {
    if (!text.trim()) return
    setState('running')
    setError(null)
    try {
      await triggerReassessItem({ scopeId, challenge: text })
      poll()
    } catch (e) {
      setState('failed')
      setError(e instanceof ApiError ? e.message : 'Failed to start the challenge')
    }
  }

  return (
    <div className="challenge-panel">
      <h4>Challenge this analysis</h4>
      <p className="challenge-hint">
        Disagree with the AI's conclusion? Say why -- the next pass must address your
        points explicitly and the result is tracked for challenge analytics.
      </p>
      <textarea
        className="decision-comment"
        placeholder="e.g. This also needs a DB migration, the AI missed that the column isn't nullable…"
        value={text}
        onChange={e => setText(e.target.value)}
        rows={2}
        disabled={state === 'running'}
      />
      <div className="decision-actions">
        <button className="challenge-btn" onClick={submit} disabled={state === 'running' || !text.trim()}>
          {state === 'running' ? 'Re-assessing…' : '⚡ Challenge'}
        </button>
      </div>
      {state === 'done' && <div className="challenge-done">✅ Fresh analysis created, addressing your challenge.</div>}
      {error && <div className="decision-error">⚠️ {error}</div>}
      <style jsx>{`
        .challenge-panel {
          display: flex;
          flex-direction: column;
          gap: 10px;
          padding: 14px;
          border: 1px solid var(--border);
          border-radius: 8px;
          background: var(--page-plane);
        }
        .challenge-panel h4 {
          font-size: 13px;
          font-weight: 600;
          color: var(--text-primary);
        }
        .challenge-hint {
          font-size: 12px;
          color: var(--text-secondary);
          line-height: 1.5;
        }
        .decision-comment {
          width: 100%;
          padding: 8px 10px;
          border: 1px solid var(--border);
          border-radius: 6px;
          background: var(--surface-1);
          color: var(--text-primary);
          font-size: 13px;
          font-family: inherit;
          resize: vertical;
        }
        .decision-actions {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .challenge-btn {
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          color: white;
          background: var(--series-1);
        }
        .challenge-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .challenge-done {
          font-size: 12px;
          color: var(--status-good);
        }
        .decision-error {
          font-size: 12px;
          color: var(--status-critical);
        }
      `}</style>
    </div>
  )
}

export function AnalysisReview() {
  const [analyses, setAnalyses] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [stageFilter, setStageFilter] = useState('SUBMITTED')
  const [fixStatusFilter, setFixStatusFilter] = useState('all')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [reassess, setReassess] = useState({ state: 'idle' })
  const pollRef = useRef(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await listMboAnalyses()
      setAnalyses(res)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not reach the orchestrator API. Is it running?')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current) }, [])

  const pollReassess = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current)
    pollRef.current = setInterval(async () => {
      try {
        const s = await getReassessStatus()
        setReassess(s)
        if (s.state !== 'running') {
          clearInterval(pollRef.current)
          pollRef.current = null
          load()
        }
      } catch { /* transient poll failure -- keep polling */ }
    }, 4000)
  }, [load])

  const startReassess = async force => {
    setError(null)
    try {
      const s = await triggerReassessRun({ includeRequirements: true, force })
      setReassess(s)
      pollReassess()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to start the re-assessment run')
    }
  }

  const stageFiltered = stageFilter === 'all'
    ? analyses
    : analyses.filter(a => a.status === stageFilter)
  const filtered = fixStatusFilter === 'all'
    ? stageFiltered
    : stageFiltered.filter(a => fixStatusOf(a) === fixStatusFilter)
  const selected = analyses.find(a => a.id === selectedId) || null
  const pendingCount = analyses.filter(a => a.status === 'SUBMITTED').length
  const fixStatusCounts = stageFiltered.reduce((acc, a) => {
    const k = fixStatusOf(a)
    acc[k] = (acc[k] || 0) + 1
    return acc
  }, {})

  return (
    <div className="analysis-review">
      <div className="review-header">
        <div>
          <h2>AI Analyses</h2>
          <p className="review-subtitle">
            Every AI-generated analysis in essms-mbo — approach, costing, timeline and the
            AI's own confidence — with accept/reject (and a voice note) recorded here for audit.
          </p>
        </div>
        <div className="reassess-controls">
          <button
            className="reassess-btn"
            onClick={() => startReassess(false)}
            disabled={reassess.state === 'running'}
            title="Assess every Issue/Requirement that has no analysis yet"
          >
            {reassess.state === 'running' ? 'Re-assessing…' : '🧠 Assess missing'}
          </button>
          <button
            className="reassess-btn force"
            onClick={() => {
              if (window.confirm('Re-assess EVERY issue and requirement (one AI call each)? Existing analyses are kept as history.')) {
                startReassess(true)
              }
            }}
            disabled={reassess.state === 'running'}
            title="Re-assess everything, even items that already have an analysis"
          >
            🔁 Re-assess all
          </button>
        </div>
      </div>

      {reassess.state === 'running' && (
        <div className="reassess-banner">⏳ Batch re-assessment in progress — one AI call per item; the list refreshes when it finishes.</div>
      )}
      {reassess.state === 'done' && (
        <div className="reassess-banner done">
          ✅ Re-assessment finished: {reassess.created ?? 0} analysed
          {(reassess.created_mechanical ?? 0) > 0 && `, ${reassess.created_mechanical} mechanical fallback`}
          {(reassess.skipped_existing ?? 0) > 0 && `, ${reassess.skipped_existing} skipped (already analysed)`}
          {(reassess.failed ?? 0) > 0 && `, ${reassess.failed} failed`}
        </div>
      )}
      {reassess.state === 'failed' && (
        <div className="reassess-banner failed">✕ Re-assessment run failed: {reassess.error}</div>
      )}
      {error && <div className="review-error" role="alert">⚠️ {error}</div>}

      <div className="review-layout">
        <div className="review-list-panel">
          <div className="list-toolbar">
            <select value={stageFilter} onChange={e => setStageFilter(e.target.value)} className="filter-select" aria-label="Filter by review stage">
              <option value="SUBMITTED">Awaiting Review ({pendingCount})</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
              <option value="all">All</option>
            </select>
            <button className="refresh-list-btn" onClick={load} title="Refresh">🔄</button>
          </div>
          <div className="fix-status-group" role="group" aria-label="Filter by whether the AI can fix it">
            {Object.keys(FIX_STATUS_CONFIG).map(key => {
              const count = key === 'all'
                ? stageFiltered.length
                : (fixStatusCounts[key] || 0)
              if (key !== 'all' && count === 0 && fixStatusFilter !== key) return null
              const cfg = FIX_STATUS_CONFIG[key]
              return (
                <button
                  key={key}
                  className={`fix-status-btn ${fixStatusFilter === key ? 'active' : ''}`}
                  style={fixStatusFilter === key ? { borderColor: cfg.color, color: cfg.color } : undefined}
                  onClick={() => setFixStatusFilter(key)}
                >
                  {cfg.label} <span className="fix-status-count">{count}</span>
                </button>
              )
            })}
          </div>
          {loading ? (
            <div className="panel-loading">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="panel-empty">
              <p>
                No analyses
                {stageFilter !== 'all' ? ' in this stage' : ''}
                {fixStatusFilter !== 'all' ? ` matching "${FIX_STATUS_CONFIG[fixStatusFilter].label}"` : (stageFilter === 'all' ? ' yet' : '')}.
              </p>
              <p className="empty-hint">Run "Assess missing" to have the AI analyse every item without one.</p>
            </div>
          ) : (
            <ul className="analysis-list">
              {filtered.map(a => {
                const scope = scopeOf(a)
                return (
                  <li key={a.id}>
                    <button
                      className={`analysis-item ${selectedId === a.id ? 'active' : ''}`}
                      onClick={() => setSelectedId(a.id)}
                    >
                      <div className="item-row">
                        <span className="item-scope">{scope.icon} {scope.type}</span>
                        <div className="item-row-chips">
                          <FixStatusChip status={fixStatusOf(a)} />
                          <StageChip status={a.status} />
                        </div>
                      </div>
                      <div className="item-title">{a.title}</div>
                      <div className="item-row">
                        <ConfidenceBar value={a.confidence} />
                      </div>
                      <div className="item-facts">
                        {a.estimatedCost != null && <span>₹{Number(a.estimatedCost).toLocaleString('en-IN')}</span>}
                        {a.estimatedHours != null && <span>{Number(a.estimatedHours)}h</span>}
                        {a.timelineDays != null && <span>{Number(a.timelineDays)}d timeline</span>}
                        {a.auditDecision?.hasAudio && <span title="Voice note recorded with the decision">🎙️</span>}
                      </div>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        <div className="review-detail-panel">
          {!selected ? (
            <div className="panel-placeholder">Select an analysis to review its full details.</div>
          ) : (
            <div className="detail">
              <div className="detail-top">
                <h3>{selected.title}</h3>
                <div className="item-row-chips">
                  <FixStatusChip status={fixStatusOf(selected)} />
                  <StageChip status={selected.status} />
                </div>
              </div>
              <div className="metric-cards">
                <div className="metric-card">
                  <span className="metric-label">AI Confidence</span>
                  <ConfidenceBar value={selected.confidence} />
                </div>
                <div className="metric-card">
                  <span className="metric-label">Estimated Cost</span>
                  <span className="metric-value">
                    {selected.estimatedCost != null ? `₹${Number(selected.estimatedCost).toLocaleString('en-IN')}` : '—'}
                  </span>
                </div>
                <div className="metric-card">
                  <span className="metric-label">Effort</span>
                  <span className="metric-value">{selected.estimatedHours != null ? `${Number(selected.estimatedHours)}h` : '—'}</span>
                </div>
                <div className="metric-card">
                  <span className="metric-label">Timeline</span>
                  <span className="metric-value">{selected.timelineDays != null ? `${Number(selected.timelineDays)} days` : '—'}</span>
                </div>
              </div>
              <div className="detail-scope">
                {(() => { const s = scopeOf(selected); return <>Attached to <strong>{s.type}</strong>{s.id && <> <code>{String(s.id).slice(0, 8)}</code></>}</> })()}
              </div>
              <AnalysisDescription text={selected.description} />

              {selected.auditDecision && (
                <div className="past-decision">
                  <span>
                    {selected.auditDecision.decision === 'approve' ? '✓ Accepted' : '✕ Rejected'} by {selected.auditDecision.decidedBy || 'unknown'}
                    {' · '}{new Date(selected.auditDecision.decidedAt).toLocaleString()}
                  </span>
                  {selected.auditDecision.hasAudio && (
                    <audio controls src={decisionAudioUrl(selected.id)} className="decision-audio" preload="none" />
                  )}
                </div>
              )}

              {selected.status === 'SUBMITTED' && (
                <DecisionPanel analysis={selected} onDecided={load} />
              )}
              {(() => { const s = scopeOf(selected); return canChallenge(s) && (
                <ChallengePanel scopeId={s.id} onChallenged={load} />
              ) })()}
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .analysis-review {
          display: flex;
          flex-direction: column;
          gap: 16px;
          height: 100%;
        }
        .review-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
        }
        .review-header h2 {
          font-size: 18px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 4px;
        }
        .review-subtitle {
          font-size: 13px;
          color: var(--text-secondary);
          max-width: 560px;
        }
        .reassess-controls {
          display: flex;
          gap: 8px;
        }
        .reassess-btn {
          padding: 8px 14px;
          border-radius: 6px;
          background: var(--series-1);
          color: white;
          font-size: 13px;
          font-weight: 500;
          white-space: nowrap;
        }
        .reassess-btn.force {
          background: var(--page-plane);
          color: var(--text-primary);
          border: 1px solid var(--border);
        }
        .reassess-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .reassess-banner {
          padding: 10px 14px;
          border-radius: 6px;
          font-size: 13px;
          background: var(--sequential-100);
          color: var(--sequential-550);
        }
        .reassess-banner.done { background: #dcfce7; color: #166534; }
        .reassess-banner.failed { background: #fee2e2; color: #991b1b; }
        .review-error {
          padding: 10px 14px;
          background: #fee2e2;
          color: #991b1b;
          border-radius: 6px;
          font-size: 13px;
        }
        .review-layout {
          display: grid;
          grid-template-columns: 380px 1fr;
          gap: 16px;
          flex: 1;
          min-height: 0;
        }
        .review-list-panel {
          display: flex;
          flex-direction: column;
          gap: 12px;
          background: var(--surface-1);
          border: 1px solid var(--border);
          border-radius: 8px;
          padding: 16px;
          overflow: hidden;
        }
        .list-toolbar {
          display: flex;
          gap: 8px;
        }
        .filter-select {
          flex: 1;
          padding: 6px 8px;
          border: 1px solid var(--border);
          border-radius: 6px;
          background: var(--page-plane);
          color: var(--text-primary);
          font-size: 12px;
        }
        .refresh-list-btn {
          padding: 6px 10px;
          border: 1px solid var(--border);
          border-radius: 6px;
          background: var(--page-plane);
        }
        .fix-status-group {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }
        .fix-status-btn {
          padding: 4px 10px;
          border: 1px solid var(--border);
          border-radius: 999px;
          background: var(--page-plane);
          color: var(--text-secondary);
          font-size: 11px;
          font-weight: 600;
          white-space: nowrap;
        }
        .fix-status-btn.active {
          background: var(--sequential-100);
        }
        .fix-status-count {
          font-variant-numeric: tabular-nums;
          opacity: 0.75;
        }
        .panel-loading, .panel-empty, .panel-placeholder {
          padding: 40px 12px;
          text-align: center;
          color: var(--text-muted);
          font-size: 13px;
        }
        .empty-hint {
          font-size: 12px;
          margin-top: 4px;
        }
        .analysis-list {
          list-style: none;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .analysis-item {
          display: flex;
          flex-direction: column;
          gap: 6px;
          width: 100%;
          text-align: left;
          padding: 10px 12px;
          border-radius: 6px;
          border: 1px solid var(--border);
          background: var(--page-plane);
        }
        .analysis-item:hover { border-color: var(--series-1); }
        .analysis-item.active {
          border-color: var(--series-1);
          background: var(--sequential-100);
        }
        .item-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }
        .item-scope {
          font-size: 11px;
          font-weight: 500;
          color: var(--text-secondary);
        }
        .item-row-chips {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .item-title {
          font-size: 13px;
          color: var(--text-primary);
          font-weight: 500;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          line-height: 1.4;
        }
        .item-facts {
          display: flex;
          gap: 10px;
          font-size: 11px;
          color: var(--text-muted);
          font-variant-numeric: tabular-nums;
        }
        .review-detail-panel {
          background: var(--surface-1);
          border: 1px solid var(--border);
          border-radius: 8px;
          padding: 20px;
          overflow-y: auto;
        }
        .detail {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .detail-top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
        }
        .detail-top h3 {
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary);
          line-height: 1.35;
        }
        .metric-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 10px;
        }
        .metric-card {
          display: flex;
          flex-direction: column;
          gap: 6px;
          padding: 10px 12px;
          border: 1px solid var(--border);
          border-radius: 8px;
          background: var(--page-plane);
        }
        .metric-label {
          font-size: 10px;
          font-weight: 700;
          color: var(--text-muted);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .metric-value {
          font-size: 18px;
          font-weight: 700;
          color: var(--text-primary);
          font-variant-numeric: tabular-nums;
        }
        .detail-scope {
          font-size: 12px;
          color: var(--text-secondary);
        }
        .detail-scope code {
          background: var(--page-plane);
          padding: 1px 6px;
          border-radius: 4px;
          font-size: 11px;
        }
        .past-decision {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
          padding: 10px 14px;
          border: 1px solid var(--border);
          border-radius: 8px;
          background: var(--page-plane);
          font-size: 13px;
          color: var(--text-primary);
        }
        .decision-audio {
          height: 32px;
          max-width: 280px;
        }
        @media (max-width: 900px) {
          .review-layout {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  )
}

function StageChip({ status }) {
  const config = STAGE_CONFIG[status] || STAGE_CONFIG.CREATED
  return (
    <span className="stage-chip" style={{ background: config.bg, color: config.color }}>
      <span className="stage-dot" style={{ background: config.color }} />
      {config.label}
      <style jsx>{`
        .stage-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 3px 10px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 600;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .stage-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
        }
      `}</style>
    </span>
  )
}

function FixStatusChip({ status }) {
  if (status === 'UNSET') return null
  const config = FIX_STATUS_CONFIG[status] || FIX_STATUS_CONFIG.UNSET
  return (
    <span className="fix-chip" style={{ color: config.color, borderColor: config.color }} title="AI fixability judgement">
      {config.label}
      <style jsx>{`
        .fix-chip {
          display: inline-flex;
          align-items: center;
          padding: 2px 8px;
          border-radius: 999px;
          border: 1px solid currentColor;
          font-size: 10px;
          font-weight: 700;
          white-space: nowrap;
          flex-shrink: 0;
        }
      `}</style>
    </span>
  )
}
